// Pure helpers for Settings → Prioritization (admin/pages/PrioritizationPage.jsx),
// kept out of the component so they can be unit-tested with node --test.

export const PRIORITY_LEVELS = [0, 1, 2, 3]

// Sorted by level ascending, then label. levels: an empty list means every
// level; search matches the label or the raw intent value, case-insensitive.
export function filterAndSortPriorities(intents, { levels = [], search = '' } = {}) {
  const q = search.trim().toLowerCase()
  return intents
    .filter((i) => levels.length === 0 || levels.includes(i.tier))
    .filter((i) => !q || i.label.toLowerCase().includes(q) || i.intent.toLowerCase().includes(q))
    .slice()
    .sort((a, b) => a.tier - b.tier || a.label.localeCompare(b.label))
}

// The confirmation dialog's wording.
export function priorityChangeMessage({ label, fromTier, toTier }) {
  return `Change ${label} from Level ${fromTier} to Level ${toTier}? This changes the order of every agent's Queue.`
}

// Quick-reply rules (2026-10-29). The key as an admin reads it: "1A · ebay ·
// any client" or "1A · m365_email · WMT".
export function quickReplyRuleKeyLabel({ company, channel, trqClientCode }) {
  return `${company} · ${channel} · ${trqClientCode ?? 'any client'}`
}

// What one rule change will do, for the confirmation dialog.
export function quickReplyChangeMessage(rule, changes) {
  const parts = []
  if ('enabled' in changes && changes.enabled !== rule.enabled) parts.push(changes.enabled ? 'turn it on' : 'turn it off')
  if ('windowMinutes' in changes && changes.windowMinutes !== rule.windowMinutes) parts.push(`change the window from ${rule.windowMinutes} to ${changes.windowMinutes} minutes`)
  if ('level' in changes && changes.level !== rule.level) parts.push(`change the level from Level ${rule.level} to Level ${changes.level}`)
  return `For ${quickReplyRuleKeyLabel(rule)}: ${parts.join(', ') || 'no change'}? This changes the order of every agent's Queue.`
}

// A minutes value the server accepts: a whole number from 1 to 10080 (a week).
export function validQuickReplyMinutes(value) {
  const n = Number(value)
  return Number.isInteger(n) && n >= 1 && n <= 10080
}

// One history row's change, in words: "Created: on, 60 min, Level 1" or
// "Window 60 → 30 min".
export function quickReplyHistoryChange(h) {
  if (h.oldEnabled === null && h.oldWindowMinutes === null && h.oldLevel === null) {
    return `Created: ${h.newEnabled ? 'on' : 'off'}, ${h.newWindowMinutes} min, Level ${h.newLevel}`
  }
  const parts = []
  if (h.oldEnabled !== h.newEnabled) parts.push(h.newEnabled ? 'Turned on' : 'Turned off')
  if (h.oldWindowMinutes !== h.newWindowMinutes) parts.push(`Window ${h.oldWindowMinutes} → ${h.newWindowMinutes} min`)
  if (h.oldLevel !== h.newLevel) parts.push(`Level ${h.oldLevel} → Level ${h.newLevel}`)
  return parts.join('; ')
}
